import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { practiceAreas } from '../../mocks/practice-areas';

const PracticeAreasPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-navy-900">
        <div className="absolute inset-0 opacity-10">
          <div
            className="w-full h-full"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23C9A961' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block px-6 py-2 border border-gold-500 rounded-full mb-6">
              <span className="text-xs font-sans uppercase tracking-widest text-gold-500">UZMANLIK ALANLARI</span>
            </div>
            <h1 className="font-serif text-5xl lg:text-6xl text-white mb-6">
              Kapsamlı Hukuki<br />Hizmetler
            </h1>
            <p className="font-sans text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
              Geniş uzmanlık alanlarımızla müvekkillerimize her türlü hukuki ihtiyaçlarında profesyonel destek sağlıyoruz
            </p>
          </motion.div>
        </div>
      </section>

      {/* Practice Areas Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practiceAreas.map((area, index) => (
              <motion.div
                key={area.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/practice-areas/${area.slug}`}
                  className="group block bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 h-full cursor-pointer"
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={area.image}
                      alt={area.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/50 to-transparent"></div>
                    <div className="absolute top-0 left-0 w-full h-1 bg-gold-500"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="w-12 h-12 flex items-center justify-center bg-gold-500 rounded-md mb-4">
                        <i className={`${area.icon} text-2xl text-white`}></i>
                      </div>
                      <h3 className="font-sans text-xl font-semibold text-white mb-2">{area.title}</h3>
                      <p className="font-sans text-sm text-white/80 line-clamp-2">{area.shortDescription}</p>
                      <div className="mt-4 inline-flex items-center text-gold-500 text-sm font-medium">
                        <span>Daha Fazla Bilgi</span>
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl lg:text-5xl text-navy-900 mb-4">Neden Hizmetlerimizi Seçmelisiniz?</h2>
            <p className="font-sans text-lg text-gray-600 max-w-2xl mx-auto">
              Hukuki hizmetlerimizin size sunduğu avantajlar
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-user-star-line',
                title: 'Uzman Ekip',
                desc: 'Her uzmanlık alanında deneyimli ve başarılı avukatlarımız',
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Stratejik Yaklaşım',
                desc: 'Her vaka için özelleştirilmiş, stratejik çözümler',
              },
              {
                icon: 'ri-time-line',
                title: 'Hızlı Yanıt',
                desc: 'Acil durumlarda hızlı ve etkili hukuki destek',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Güvenilirlik',
                desc: 'En yüksek etik standartlara bağlı profesyonel hizmet',
              },
              {
                icon: 'ri-global-line',
                title: 'Uluslararası Deneyim',
                desc: 'Sınır ötesi işlemlerde geniş deneyim ve uzmanlık',
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Müvekkil Memnuniyeti',
                desc: 'Müvekkil odaklı yaklaşım ve yüksek memnuniyet oranı',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-md"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-gold-500/10 rounded-lg mb-6">
                  <i className={`${item.icon} text-3xl text-gold-500`}></i>
                </div>
                <h3 className="font-sans text-xl font-semibold text-navy-900 mb-3">{item.title}</h3>
                <p className="font-sans text-sm text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
              Hukuki Danışmanlığa İhtiyacınız mı Var?
            </h2>
            <p className="font-sans text-lg text-white/80 mb-10 max-w-2xl mx-auto">
              Deneyimli avukatlarımız, hukuki ihtiyaçlarınız için size yardımcı olmaya hazır. Bugün bizimle iletişime geçin.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-10 py-5 bg-gold-500 text-white font-sans text-base font-medium rounded-md hover:bg-gold-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <span>Danışmanlık Talep Edin</span>
              <i className="ri-arrow-right-line ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PracticeAreasPage;